import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  useTheme,
  THEMES,
  DARK_TEXT,
  LIGHT_TEXT,
} from "../state/themeState.js";
import { FlatLogo } from "./FlatLogo.js";
import styles from "../styles/components/WelcomeModal.module.css";

const STORAGE_KEY = "td_onboarding_shown";

export function useWelcomeModal() {
  const [show, setShow] = useState(() => !localStorage.getItem(STORAGE_KEY));

  function dismiss() {
    localStorage.setItem(STORAGE_KEY, "1");
    setShow(false);
  }

  return { show, dismiss };
}

interface Props {
  onDismiss: () => void;
}

const STEPS = [
  {
    title: "Welcome to",
    body: "A curated news reader with 25+ handpicked rooms across tech, science, world news, culture, and more. No algorithm — just signal.",
    cta: "Next →",
  },
  {
    title: "Rooms & Favorites",
    body: "Each room is a curated feed. Browse rooms using the sidebar panel, and star the ones you love to pin them to the top for quick access.",
    cta: "Next →",
  },
  {
    title: "Make it yours",
    body: "Sign in to create your own rooms, add custom RSS feeds, and sync your reading state across devices. ",
    cta: "Next →",
  },
  {
    title: "Works everywhere",
    body: "Twice Daily is desktop and mobile friendly. Bookmark it. Add it to your home screen. Enjoy.",
    cta: null,
  },
];

export function WelcomeModal({ onDismiss }: Props) {
  const [step, setStep] = useState(0);
  const currentStep = STEPS[step]!;
  const { themeIndex, hoveredThemeIndex, isDark } = useTheme();
  const primary =
    THEMES[hoveredThemeIndex ?? themeIndex]?.[isDark ? "dark" : "light"]
      .primary;
  const textColor = isDark ? DARK_TEXT : LIGHT_TEXT;

  function advance() {
    if (step < STEPS.length - 1) setStep(step + 1);
    else onDismiss();
  }

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onDismiss();
      }}
    >
      <div className={styles.modal} role="dialog" aria-modal="true">
        <button
          type="button"
          className={styles.close}
          onClick={onDismiss}
          aria-label="Close"
        >
          ×
        </button>

        <div className={styles.content}>
          <h2 className={styles.title}>
            {step === 0 ? (
              <span className={styles.titleWithLogo}>
                {currentStep.title}
                <FlatLogo
                  size={20}
                  accentColor={primary}
                  textColor={textColor}
                  text="Twice Daily"
                  separation={72}
                />
              </span>
            ) : (
              currentStep.title
            )}
          </h2>
          <p className={styles.body}>{currentStep.body}</p>

          {step === STEPS.length - 1 && (
            <div className={styles.finalActions}>
              <button
                type="button"
                className={styles.ctaBtn}
                onClick={onDismiss}
              >
                Enough! Let me in!
              </button>
            </div>
          )}

          {currentStep.cta && (
            <button type="button" className={styles.nextBtn} onClick={advance}>
              {currentStep.cta}
            </button>
          )}
        </div>

        <div className={styles.dots}>
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`${styles.dot} ${i === step ? styles.dotActive : ""}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
